import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * <gift-wrap-component>
 *
 * A product-page add-on with an explicit Add button (not a passive checkbox):
 *   • click "Add"      → adds the gift-wrap PRODUCT to the cart as its own line
 *   • click "✓ Added"  → removes that line again
 *
 * The button mirrors cart state: it shows "✓ Added" whenever the wrap is in the
 * cart (including on load, or if the shopper removes it from the drawer). The
 * cart drawer / icon re-render via the theme's own CartLinesUpdateEvent contract.
 *
 * The optional gift message is separate: it rides the MAIN product's line item
 * via a form-associated <textarea> whose `name` we toggle so an empty box never
 * writes a blank property. So a shopper can leave a message with or without wrap.
 */
class GiftWrapComponent extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.button = this.querySelector('[data-gift-wrap-button]');
    this.buttonLabel = this.querySelector('[data-gift-wrap-button-label]');
    this.messageInput = this.querySelector('[data-gift-wrap-message]');
    this.variantId = this.dataset.wrapVariantId || '';
    this.messageName = this.dataset.messageName || '';
    this.openOnAdd = this.dataset.openOnAdd !== 'false';
    this.labelAdd = this.button?.dataset.labelAdd || 'Add';
    this.labelAdded = this.button?.dataset.labelAdded || 'Added';

    if (this.messageInput && this.messageName) {
      this.messageInput.addEventListener('input', this.#syncMessageName);
      this.#syncMessageName();
    }

    if (this.button && this.variantId) {
      this.button.addEventListener('click', this.#onClick);
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
      this.#syncFromCart();
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
    this.messageInput?.removeEventListener('input', this.#syncMessageName);
  }

  #syncMessageName = () => {
    if (!this.messageInput) return;
    if (this.messageInput.value.trim().length > 0) {
      this.messageInput.setAttribute('name', this.messageName);
    } else {
      this.messageInput.removeAttribute('name');
    }
  };

  get #added() {
    return this.button?.getAttribute('aria-pressed') === 'true';
  }

  #setState(added) {
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', added ? 'true' : 'false');
    if (this.buttonLabel) this.buttonLabel.textContent = added ? this.labelAdded : this.labelAdd;
    this.button.setAttribute('title', added ? 'Remove gift wrap' : '');
  }

  #onClick = () => {
    if (this.#busy) return;
    this.#run(this.#added ? this.#removeWrap() : this.#addWrap());
  };

  /** @param {Promise<void>} work */
  async #run(work) {
    this.#busy = true;
    if (this.button) this.button.disabled = true;
    try {
      await work;
    } finally {
      this.#busy = false;
      if (this.button) this.button.disabled = false;
    }
  }

  /** Reflect existing cart state so the button matches reality. */
  async #syncFromCart() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      this.#setState(
        (cart.items || []).some((item) => String(item.variant_id) === String(this.variantId))
      );
    } catch {
      /* offline / cart unavailable — leave the button as-is */
    }
  }

  /** Re-sync when the cart changes elsewhere (e.g. removed in the drawer). */
  #onCartChange = (event) => {
    event.promise
      ?.then((resolved) => {
        // Skip our own renders — those already set the button state directly.
        if (resolved?.detail?.source === 'gift-wrap-component') return;
        this.#syncFromCart();
      })
      .catch(() => {});
  };

  #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
  }

  async #addWrap() {
    const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    /** @type {any} */
    const body = { items: [{ id: Number(this.variantId), quantity: 1 }] };
    const sections = this.#sectionIds();
    if (sections.length) body.sections = sections.join(',');

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();

    if (result.status) {
      // e.g. wrap sold out — stay in the "Add" state.
      console.warn('[gift-wrap] could not add wrap:', result.message || result.description);
      return;
    }

    this.#setState(true);
    await this.#render(result.sections, this.openOnAdd ? 'add' : 'update');
  }

  async #removeWrap() {
    /** @type {any} */
    const body = { id: this.variantId, quantity: 0 };
    const sections = this.#sectionIds();
    if (sections.length) body.sections = sections.join(',');

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();

    if (result.status) {
      console.warn('[gift-wrap] could not remove wrap:', result.message || result.description);
      return;
    }

    this.#setState(false);
    await this.#render(result.sections, 'update');
  }

  /**
   * Re-render the drawer + cart sections via the theme's own event contract.
   * @param {Record<string,string>|undefined} sections
   * @param {'add'|'update'} action  'add' lets the drawer auto-open like a normal ATC
   */
  async #render(sections, action) {
    const fullCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'product',
        lines: [{ merchandiseId: String(this.variantId), quantity: action === 'add' ? 1 : 0 }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
      detail: {
        items: fullCart.items,
        sections,
        source: 'gift-wrap-component',
        didError: false,
      },
    });
  }
}

if (!customElements.get('gift-wrap-component')) {
  customElements.define('gift-wrap-component', GiftWrapComponent);
}
